import { useEffect, useMemo, useRef, useState } from "react";
import { ChevronDownIcon } from "lucide-react";
import { HiOutlineInformationCircle } from "react-icons/hi";
import { toast } from "sonner";

import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";

import {
  getFollowUpStampPaperFromVendor,
  updateFollowUpStampPaperFromVendor,
  type FollowUpStampPaperFromVendorApplication,
  type UpdateFollowUpStampPaperFromVendorPayload,
} from "../../api/followUpFiling";

export const FOLLOW_UP_STAMP_PAPER_FROM_VENDOR_FORM_ID =
  "follow-up-stamp-paper-from-vendor-form";

const LABEL_STYLE = "text-xs font-normal leading-5 flex items-center";
const LABEL_FEATURES = { fontFeatureSettings: "'ss01' on, 'cv01' on" } as const;
const INPUT_BASE =
  "box-border flex flex-row items-center justify-between gap-2.5 px-[10px] py-2 h-9 min-h-[44px] md:min-h-[36px] w-full bg-white border border-[#EEEEEE] rounded-[6px] focus-within:ring-1 focus-within:ring-[#222]";
const TEXT_MUTED =
  "text-[12px] md:text-xs font-normal leading-5 text-[#6D6D6D]";
const TEXT_CLASS =
  "text-[12px] md:text-xs font-normal leading-5 text-[#222222]";

type FollowUpStampPaperFromVendorViewProps = {
  leadId?: string | null;
  onSuccess?: (leadId: string) => void;
};

export default function FollowUpStampPaperFromVendorView({
  leadId,
  onSuccess,
}: FollowUpStampPaperFromVendorViewProps) {
  const [applications, setApplications] = useState<
    FollowUpStampPaperFromVendorApplication[]
  >([]);
  const [paymentStatusDisplay, setPaymentStatusDisplay] = useState<string>("");

  const [loading, setLoading] = useState<boolean>(!!leadId);
  const [saving, setSaving] = useState(false);

  const autoSaveDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(
    null
  );

  const [calcTooltipOpen, setCalcTooltipOpen] = useState(false);
  const calcTooltipRef = useRef<HTMLDivElement>(null);

  const totalAmountPayable = useMemo(
    () =>
      applications.reduce(
        (sum, app) => sum + (Number(app.amountPayableToStampVendor) || 0),
        0
      ),
    [applications]
  );

  const totalStampPapers = useMemo(
    () =>
      applications.reduce(
        (sum, app) => sum + (Number(app.numberOfStampPapersForPoa) || 0),
        0
      ),
    [applications]
  );

  const derivedPoaRate = useMemo(
    () =>
      totalStampPapers > 0
        ? Math.round((totalAmountPayable / totalStampPapers) * 100) / 100
        : 0,
    [totalAmountPayable, totalStampPapers]
  );

  useEffect(() => {
    if (!calcTooltipOpen) return;
    const handler = (e: MouseEvent) => {
      if (
        calcTooltipRef.current &&
        !calcTooltipRef.current.contains(e.target as Node)
      ) {
        setCalcTooltipOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [calcTooltipOpen]);

  useEffect(() => {
    if (!leadId) {
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    getFollowUpStampPaperFromVendor(leadId)
      .then((data) => {
        if (cancelled) return;
        setApplications(data.applications ?? []);
        setPaymentStatusDisplay(data.paymentStatusDisplay ?? "");
      })
      .catch((err) => {
        if (cancelled) return;
        toast.error(
          err instanceof Error
            ? err.message
            : "Failed to load stamp paper from vendor details"
        );
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [leadId]);

  const buildPayload = (
    nextApplications: FollowUpStampPaperFromVendorApplication[]
  ): UpdateFollowUpStampPaperFromVendorPayload => ({
    paymentPerApplication: nextApplications.map((app) => ({
      applicationId: app.applicationId,
      tmNo: app.tmNo,
      amountPayableToStampVendor: Number(app.amountPayableToStampVendor) || 0,
      paidToVendor: app.paidToVendor ?? undefined,
    })),
  });

  const scheduleAutoSave = (
    nextApplications: FollowUpStampPaperFromVendorApplication[]
  ) => {
    if (!leadId) return;
    if (autoSaveDebounceRef.current) clearTimeout(autoSaveDebounceRef.current);
    autoSaveDebounceRef.current = setTimeout(() => {
      updateFollowUpStampPaperFromVendor(leadId, buildPayload(nextApplications))
        .then((data) => {
          setApplications(data.applications ?? []);
          setPaymentStatusDisplay(data.paymentStatusDisplay ?? "");
        })
        .catch(() => {
          // Auto-save errors are non-blocking; the explicit submit will surface real issues.
        });
    }, 600);
  };

  useEffect(() => {
    return () => {
      if (autoSaveDebounceRef.current)
        clearTimeout(autoSaveDebounceRef.current);
    };
  }, []);

  const updateAmount = (index: number, value: string) => {
    setApplications((prev) => {
      const next = [...prev];
      const parsed = parseInt(value, 10);
      next[index] = {
        ...next[index],
        amountPayableToStampVendor: Number.isFinite(parsed) && parsed >= 0 ? parsed : 0,
      };
      scheduleAutoSave(next);
      return next;
    });
  };

  const updatePaid = (index: number, value: string) => {
    setApplications((prev) => {
      const next = [...prev];
      const paid =
        value === "yes" ? true : value === "no" ? false : null;
      next[index] = {
        ...next[index],
        paidToVendor: paid,
      };
      scheduleAutoSave(next);
      return next;
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!leadId || saving) return;
    if (applications.length === 0) {
      toast.error(
        "No applications found. Please complete the previous step first."
      );
      return;
    }
    const missingPaid = applications.find((app) => app.paidToVendor === null);
    if (missingPaid) {
      toast.error(
        `Select Paid to Vendor for TM No. ${missingPaid.tmNo}.`
      );
      return;
    }
    const negativeAmount = applications.find(
      (app) => Number(app.amountPayableToStampVendor) < 0
    );
    if (negativeAmount) {
      toast.error(
        `Amount Payable to Vendor must be 0 or greater for TM No. ${negativeAmount.tmNo}.`
      );
      return;
    }
    setSaving(true);
    try {
      const data = await updateFollowUpStampPaperFromVendor(
        leadId,
        buildPayload(applications)
      );
      setApplications(data.applications ?? []);
      setPaymentStatusDisplay(data.paymentStatusDisplay ?? "");
      toast.success("Stamp paper from vendor saved successfully.");
      onSuccess?.(leadId);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  if (!leadId) {
    return (
      <div
        className="flex flex-col gap-[22px] w-full max-w-[1085px] justify-center py-12"
        style={{ fontFamily: "Inter, sans-serif" }}
      >
        <p className="text-sm text-[#6D6D6D]">
          Please complete the previous steps (select or create a lead) to access
          this form.
        </p>
      </div>
    );
  }

  if (loading) {
    return (
      <div
        className="flex flex-col gap-[22px] w-full max-w-[1085px] justify-center py-12"
        style={{ fontFamily: "Inter, sans-serif" }}
      >
        <p className="text-sm text-[#6D6D6D]">
          Loading stamp paper from vendor details...
        </p>
      </div>
    );
  }

  return (
    <form
      id={FOLLOW_UP_STAMP_PAPER_FROM_VENDOR_FORM_ID}
      onSubmit={handleSubmit}
      className="flex flex-col gap-4 md:gap-5 w-full max-w-[1085px]"
      style={{ fontFamily: "Inter, sans-serif" }}
    >
      {/* Top summary: Total Amount Payable | Total Number of Stamp Papers for PoA */}
      <div className="flex flex-col md:flex-row md:flex-wrap gap-4 md:gap-5 w-full">
        <div
          className="flex flex-col flex-1 min-w-0 gap-1"
          style={{ minHeight: 60 }}
        >
          <div className="flex items-center gap-1">
            <label
              className={LABEL_STYLE}
              style={{ ...LABEL_FEATURES, color: "#222222" }}
            >
              Amount Payable to vendor (Auto calc)
            </label>
            <div className="relative" ref={calcTooltipRef}>
              <button
                type="button"
                onClick={() => setCalcTooltipOpen((o) => !o)}
                className="flex items-center justify-center text-[#6D6D6D] hover:text-[#222222]"
                aria-label="Show calculation"
              >
                <HiOutlineInformationCircle
                  className="w-4 h-4"
                  strokeWidth={1.5}
                />
              </button>
              {calcTooltipOpen && (
                <div
                  className="absolute left-0 top-full mt-1 z-30 w-72 rounded-[8px] border border-[#EEEEEE] bg-white shadow-lg p-3 text-[12px] leading-5 text-[#222222]"
                  style={{ fontFamily: "Inter, sans-serif" }}
                >
                  <p className="text-xs font-semibold text-[#101111] mb-2">
                    Calculation Breakdown
                  </p>
                  <p className="text-[11px] text-[#6D6D6D] mb-2">
                    Sum of (PoA Stamp Papers × PoA Stamp Paper Rate) across
                    all applications. The rate is configured in App Settings.
                  </p>
                  <div className="flex flex-col gap-1">
                    <div className="flex justify-between">
                      <span className="text-[#6D6D6D]">
                        PoA Stamps ({totalStampPapers} ×{" "}
                        {derivedPoaRate > 0
                          ? `Rs. ${derivedPoaRate.toLocaleString("en-IN")}`
                          : "PoA Rate"}
                        )
                      </span>
                      <span>
                        Rs. {totalAmountPayable.toLocaleString("en-IN")}/-
                      </span>
                    </div>
                    <div className="border-t border-[#222] pt-1 mt-1 flex justify-between font-semibold text-[#101111]">
                      <span>Auto-calculated</span>
                      <span>
                        Rs. {totalAmountPayable.toLocaleString("en-IN")}/-
                      </span>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
          <div
            className={INPUT_BASE}
            style={{ fontFeatureSettings: "'ss01' on, 'cv01' on" }}
          >
            <span className={`shrink-0 ${TEXT_MUTED}`}>Rs.</span>
            <span className={`flex-1 min-w-0 text-right ${TEXT_CLASS}`}>
              {totalAmountPayable}
            </span>
          </div>
        </div>

        <div
          className="flex flex-col flex-1 min-w-0 gap-1"
          style={{ minHeight: 60 }}
        >
          <label
            className={LABEL_STYLE}
            style={{ ...LABEL_FEATURES, color: "#222222" }}
          >
            Number of Stamp Papers for PoA
          </label>
          <div
            className={INPUT_BASE}
            style={{ fontFeatureSettings: "'ss01' on, 'cv01' on" }}
          >
            <span className={`flex-1 min-w-0 ${TEXT_CLASS}`}>
              {totalStampPapers || 0}
            </span>
          </div>
        </div>
      </div>

      {/* Per-application table: Class | TM No | Amount Payable | Paid to Vendor */}
      {applications.length > 0 && (
        <div
          className="flex flex-col w-full"
          style={{
            fontFamily: "Inter, sans-serif",
            fontFeatureSettings: "'ss01' on, 'cv01' on",
          }}
        >
          <div className="hidden lg:grid lg:grid-cols-[160px_230px_1fr_220px] gap-5 items-center shrink-0 text-[#222] text-sm font-medium leading-5 mb-4">
            <div>Classes</div>
            <div>TM No.</div>
            <div>Amount Payable to Vendor</div>
            <div>Paid to Vendor</div>
          </div>
          <div className="flex flex-col gap-3">
            {applications.map((app, idx) => {
              const paidValue =
                app.paidToVendor === true
                  ? "yes"
                  : app.paidToVendor === false
                    ? "no"
                    : "__none__";
              return (
                <div
                  key={app.applicationId}
                  className="grid grid-cols-1 lg:grid-cols-[160px_230px_1fr_220px] gap-3 lg:gap-5 lg:items-center w-full p-4 lg:p-0 rounded-[6px] border border-[#EEE] lg:border-0 bg-white lg:bg-transparent"
                >
                  {/* Class */}
                  <div className="flex flex-col gap-1">
                    <span className="lg:hidden text-xs font-normal text-[#222222]">
                      Classes
                    </span>
                    <div className="flex w-full items-center justify-between gap-2 min-h-[44px] lg:min-h-0 bg-[#f7f7f7] border border-[#eee] rounded-[6px] p-2.5">
                      <span className="text-[12px] lg:text-sm text-[#222] whitespace-nowrap">
                        {app.tmNo ? `TM ${app.tmNo}` : "—"}
                      </span>
                      <ChevronDownIcon className="w-4 h-4 shrink-0 text-[#6D6D6D]" />
                    </div>
                  </div>

                  {/* TM No */}
                  <div className="flex flex-col gap-1 w-full">
                    <span className="lg:hidden text-xs font-normal text-[#222222]">
                      TM No.
                    </span>
                    <div className="flex items-center h-9 min-h-[44px] lg:min-h-[36px] w-full border border-[#eee] rounded-[6px] px-2.5 py-2 text-[12px] lg:text-xs bg-white">
                      <span className={app.tmNo ? "text-[#222]" : "text-[#6D6D6D]"}>
                        {app.tmNo || "—"}
                      </span>
                    </div>
                  </div>

                  {/* Amount Payable per application */}
                  <div className="flex flex-col gap-1 w-full">
                    <span className="lg:hidden text-xs font-normal text-[#222222]">
                      Amount Payable to Vendor
                    </span>
                    <div
                      className={INPUT_BASE}
                      style={{ fontFeatureSettings: "'ss01' on, 'cv01' on" }}
                    >
                      <span className={`shrink-0 ${TEXT_MUTED}`}>Rs.</span>
                      <input
                        type="number"
                        min={0}
                        value={app.amountPayableToStampVendor}
                        onChange={(e) => updateAmount(idx, e.target.value)}
                        className="flex-1 min-w-0 bg-transparent border-0 text-[12px] lg:text-xs font-normal leading-5 text-[#222222] focus:outline-none focus:ring-0 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                        style={{ fontFeatureSettings: "'ss01' on, 'cv01' on" }}
                        placeholder="0"
                      />
                    </div>
                  </div>

                  {/* Paid to Vendor */}
                  <div className="flex flex-col gap-1 w-full">
                    <span className="lg:hidden text-xs font-normal text-[#222222]">
                      Paid to Vendor
                    </span>
                    <Select
                      value={paidValue}
                      onValueChange={(v) => updatePaid(idx, v)}
                    >
                      <SelectTrigger className="h-9 min-h-[44px] lg:min-h-[36px] w-full border border-[#eee] rounded-[6px] px-2.5 py-2 text-[12px] lg:text-xs text-[#222] data-[placeholder]:text-[#6D6D6D]">
                        <SelectValue placeholder="Select" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="__none__">Select</SelectItem>
                        <SelectItem value="yes">Yes</SelectItem>
                        <SelectItem value="no">No</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Lead-level payment status display */}
      {paymentStatusDisplay ? (
        <p
          className="text-xs font-medium leading-5 text-[#222222] mt-0.5"
          style={{ fontFeatureSettings: "'ss01' on, 'cv01' on" }}
          role="status"
        >
          Status: {paymentStatusDisplay}
        </p>
      ) : null}
    </form>
  );
}
